/**
 * InstallAppBanner (v6.34.15) — Smart "Install the app" banner.
 *
 * Detects whether the visitor is already running MOSAIC as an installed PWA
 * (via `window.matchMedia('(display-mode: standalone)').matches` and the iOS
 * `navigator.standalone` flag) and renders NOTHING in that case. For browser
 * users, it shows a slim dismissible bottom banner with platform-aware install
 * guidance:
 *
 *   - Android (Chrome) — captures the `beforeinstallprompt` event and triggers
 *     the native install prompt on click (one-tap install).
 *   - iOS (Safari)     — opens a modal with "Share → Add to Home Screen" copy.
 *   - Desktop          — opens a modal pointing at the address-bar install icon.
 *   - Other            — generic copy.
 *
 * Dismissal is remembered for 30 days via localStorage so the banner doesn't
 * nag users who chose not to install.
 *
 * Mounted in dashboard footers (SuperAdmin / Teacher / District). NOT mounted
 * on the student exam surface (no marketing during a live exam).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Smartphone, X } from 'lucide-react';

const LS_DISMISS_KEY = 'mosaic_install_banner_dismissed_at';
const DISMISS_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

const detectPlatform = () => {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  // iPadOS 13+ identifies as Mac; sniff for touch.
  const isIPadOS = ua.includes('Macintosh') && navigator.maxTouchPoints > 1;
  if (isIOS || isIPadOS) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'desktop';
};

const isStandalone = () => {
  if (typeof window === 'undefined') return false;
  try {
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true;
  } catch (_e) { /* old browsers */ }
  // iOS Safari sets navigator.standalone when launched from home screen.
  if (typeof navigator !== 'undefined' && navigator.standalone === true) return true;
  return false;
};

const isRecentlyDismissed = () => {
  try {
    const raw = window.localStorage.getItem(LS_DISMISS_KEY);
    if (!raw) return false;
    const dismissedAt = parseInt(raw, 10);
    if (!Number.isFinite(dismissedAt)) return false;
    return (Date.now() - dismissedAt) < DISMISS_TTL_MS;
  } catch (_e) {
    return false;
  }
};

export const InstallAppBanner = () => {
  const [visible, setVisible] = useState(false);
  const [platform, setPlatform] = useState('unknown');
  const [modalOpen, setModalOpen] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    if (isStandalone()) return;            // Already installed — never render.
    if (isRecentlyDismissed()) return;     // User dismissed in last 30 days.
    setPlatform(detectPlatform());
    setVisible(true);

    // Capture Android Chrome's install prompt for one-tap install.
    const onBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);

    // Hide if the user installs while the banner is up.
    const onInstalled = () => setVisible(false);
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const dismiss = useCallback(() => {
    try { window.localStorage.setItem(LS_DISMISS_KEY, String(Date.now())); } catch (_e) { /* private mode */ }
    setVisible(false);
    setModalOpen(false);
  }, []);

  const handleInstallClick = useCallback(async () => {
    // Android Chrome — one-tap install via captured prompt.
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        const choice = await deferredPrompt.userChoice;
        if (choice && choice.outcome === 'accepted') dismiss();
      } catch (_e) {
        // Fall through to modal copy.
        setModalOpen(true);
      } finally {
        setDeferredPrompt(null);
      }
      return;
    }
    // iOS Safari + Desktop + fallback — show platform-specific instructions.
    setModalOpen(true);
  }, [deferredPrompt, dismiss]);

  if (!visible) return null;

  return (
    <>
      <div
        className="fixed bottom-0 inset-x-0 z-40 md:left-auto md:right-6 md:bottom-6 md:max-w-md bg-indigo-600 text-white shadow-2xl border-t border-indigo-800 md:border md:rounded-lg flex items-center gap-3 px-4 py-3"
        data-testid="install-app-banner"
        role="dialog"
        aria-label="Install MOSAIC as an app"
      >
        <Smartphone size={22} className="flex-shrink-0" aria-hidden="true" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold">Install MOSAIC as an app</div>
          <div className="text-xs text-indigo-100">Launches full-screen — no browser bar.</div>
        </div>
        <button
          type="button"
          onClick={handleInstallClick}
          className="text-sm font-bold px-3 py-1.5 bg-white text-indigo-700 rounded hover:bg-indigo-50 transition-colors"
          data-testid="install-app-banner-install-btn"
        >
          Install
        </button>
        <button
          type="button"
          onClick={dismiss}
          className="p-1 text-indigo-100 hover:text-white transition-colors"
          aria-label="Dismiss install banner"
          data-testid="install-app-banner-dismiss-btn"
        >
          <X size={18} />
        </button>
      </div>

      {modalOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-end md:items-center justify-center p-4"
          onClick={() => setModalOpen(false)}
          data-testid="install-app-banner-modal-overlay"
        >
          <div
            className="bg-white rounded-t-lg md:rounded-lg max-w-md w-full p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="install-app-banner-modal"
          >
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <div className="text-xs font-bold uppercase tracking-wide text-indigo-600 mb-1">
                  Install MOSAIC
                </div>
                <div className="text-lg font-bold text-slate-900">
                  {platform === 'ios' && 'On iPhone & iPad'}
                  {platform === 'android' && 'On Android'}
                  {platform === 'desktop' && 'On your computer'}
                  {platform === 'unknown' && 'Add to your device'}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="text-slate-400 hover:text-slate-700"
                aria-label="Close install instructions"
                data-testid="install-app-banner-modal-close"
              >
                <X size={18} />
              </button>
            </div>

            {platform === 'ios' && (
              <ol className="space-y-3 text-sm text-slate-700" data-testid="install-instructions-ios">
                <li><b>1.</b> Make sure you're in <b>Safari</b> (Chrome on iOS won't work).</li>
                <li><b>2.</b> Tap the <b>Share button</b> (square with up arrow at the bottom).</li>
                <li><b>3.</b> Scroll down → tap <b>"Add to Home Screen"</b>.</li>
                <li><b>4.</b> Tap <b>Add</b> in the top-right corner.</li>
              </ol>
            )}
            {platform === 'android' && (
              <ol className="space-y-3 text-sm text-slate-700" data-testid="install-instructions-android">
                <li><b>1.</b> Tap the <b>⋮ (three-dot menu)</b> in the top-right of Chrome.</li>
                <li><b>2.</b> Tap <b>"Install app"</b> or <b>"Add to Home screen"</b>.</li>
                <li><b>3.</b> Tap <b>Install</b> to confirm.</li>
              </ol>
            )}
            {platform === 'desktop' && (
              <ol className="space-y-3 text-sm text-slate-700" data-testid="install-instructions-desktop">
                <li><b>1.</b> Look for the <b>install icon</b> in the address bar (monitor with a down arrow).</li>
                <li><b>2.</b> Click it → click <b>Install</b>.</li>
                <li>Or: Chrome/Edge menu → <b>Apps → Install MOSAIC</b>.</li>
              </ol>
            )}
            {platform === 'unknown' && (
              <p className="text-sm text-slate-700" data-testid="install-instructions-unknown">
                Use your browser's "Add to Home Screen" or "Install app" option. MOSAIC will launch full-screen with no browser bar.
              </p>
            )}

            <button
              type="button"
              onClick={dismiss}
              className="mt-6 w-full text-sm text-slate-500 hover:text-slate-700"
              data-testid="install-app-banner-modal-dismiss-30d"
            >
              Don't show this again for 30 days
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default InstallAppBanner;
